import { z } from 'zod';
import type { BlockItem, DashboardConfig, WidgetConfig } from '../types/dashboard-config.js';
import type { CanonicalWidgetData, ChartWidgetData } from '../types/dashboard-data.js';
import type { RootNode } from '../types/rich-text.js';

/** Flattens every widget in a DashboardConfig, including those nested inside sections. */
export function collectAllWidgets(config: DashboardConfig): WidgetConfig[] {
  const widgets: WidgetConfig[] = [];
  for (const block of config.blocks) {
    collectWidgetsFromBlock(block.items, widgets);
  }
  return widgets;
}

function collectWidgetsFromBlock(items: BlockItem[], widgets: WidgetConfig[]): void {
  for (const item of items) {
    if (item.type === 'widget') {
      widgets.push(item.widget);
    } else {
      for (const bw of item.section.widgets) {
        widgets.push(bw.widget);
      }
    }
  }
}

// ─── Widget Snapshot ──────────────────────────────────────────────────────────
//
// Compact, agent-facing projections of a widget's resolved CanonicalWidgetData.
// Used by chat-api's page-snapshot tool to summarize a dashboard page without
// forwarding full chart/table payloads or markup.

const MAX_TEXT_SNAPSHOT_LENGTH = 2000;
const MAX_DATA_TABLE_PREVIEW_ROWS = 25;
const DEFAULT_MAX_SNAPSHOT_BYTES = 10_000;

const ChartSeriesPointSchema = z.object({ x: z.union([z.string(), z.number()]), y: z.number() });
const ChartSeriesSchema = z.object({
  label: z.string().optional(),
  points: z.array(ChartSeriesPointSchema),
});
const StatGridItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  unit: z.string().optional(),
});

export const WidgetSnapshotEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('series_chart'),
    widgetId: z.string(),
    title: z.string(),
    xAxisTitle: z.string().optional(),
    yAxisTitle: z.string().optional(),
    series: z.array(ChartSeriesSchema),
  }),
  z.object({
    kind: z.literal('data_table'),
    widgetId: z.string(),
    title: z.string(),
    columns: z.array(z.string()),
    /** Row labels for `previewRows`, in the same order — capped to the same row count. */
    rowLabels: z.array(z.string()),
    rowCount: z.number().int(),
    previewRows: z.array(z.array(z.string().nullable())),
  }),
  z.object({
    kind: z.literal('stat_grid'),
    widgetId: z.string(),
    title: z.string(),
    items: z.array(StatGridItemSchema),
  }),
  z.object({
    kind: z.literal('text'),
    widgetId: z.string(),
    title: z.string(),
    content: z.string(),
  }),
  z.object({
    kind: z.literal('quote'),
    widgetId: z.string(),
    title: z.string(),
    speaker: z.string().optional(),
    content: z.string(),
  }),
  z.object({
    kind: z.literal('unsupported_widget_type'),
    widgetId: z.string(),
    title: z.string(),
    widgetType: z.string(),
  }),
  z.object({
    kind: z.literal('no_data'),
    widgetId: z.string(),
    title: z.string(),
  }),
]);
export type WidgetSnapshotEntry = z.infer<typeof WidgetSnapshotEntrySchema>;

// NOTE: `pageId` is required and numeric because Page-backed dashboards are the only supported source today.
// Legacy segment dashboards are keyed by `segmentIdentifier` (a string) and have no page ID, so when the legacy
// provider is reintroduced (see PageSnapshotRef in chat-api's pages/snapshot.provider.base.ts) this schema must
// change with it — e.g. make `pageId` optional or replace it with a generic ref/id. That change is deliberately
// deferred: this is the agent-facing contract, so it should be shaped against the real legacy provider, not guessed.
export const DashboardPageSnapshotSchema = z.object({
  pageId: z.number().int(),
  pageName: z.string(),
  widgets: z.array(WidgetSnapshotEntrySchema),
  /** Set when widgets were dropped to stay under the serialized size budget. */
  truncated: z.boolean().optional(),
  omittedWidgetCount: z.number().int().optional(),
});
export type DashboardPageSnapshot = z.infer<typeof DashboardPageSnapshotSchema>;

function truncateText(value: string, maxLength = MAX_TEXT_SNAPSHOT_LENGTH): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

// Column/row names are multi-level header paths (grouped headers use shared ancestor segments); collapse to one label.
function joinLabelPath(path: (string | null)[]): string {
  return path.filter((segment): segment is string => segment !== null).join(' / ');
}

// Walks a Remirror/ProseMirror node, collecting visible text and any mention-style label attrs.
function extractPlainTextFromNode(node: unknown): string {
  if (typeof node !== 'object' || node === null) return '';
  const { type, text, attrs, content } = node as {
    type?: string;
    text?: string;
    attrs?: { label?: string };
    content?: unknown[];
  };
  if (type === 'text' && typeof text === 'string') return text;
  if (typeof attrs?.label === 'string') return attrs.label;
  if (Array.isArray(content)) return content.map(extractPlainTextFromNode).join('');
  return '';
}

// Rich-text content is a serialized Remirror doc, not plain text — parse it before truncating, falling back to empty like the renderer does on malformed JSON.
function extractRichTextContent(serializedDoc: string): string {
  try {
    return extractPlainTextFromNode(JSON.parse(serializedDoc) as RootNode);
  } catch {
    return '';
  }
}

function projectChartSnapshot(widgetId: string, title: string, chartData: ChartWidgetData): WidgetSnapshotEntry {
  switch (chartData.type) {
    case 'bar':
    case 'stacked-bar':
      return {
        kind: 'series_chart',
        widgetId,
        title,
        xAxisTitle: chartData.chart.xAxisTitle,
        yAxisTitle: chartData.chart.yAxisTitle,
        series: [{ points: chartData.chart.data.map((p) => ({ x: p.x, y: p.y })) }],
      };
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return {
        kind: 'series_chart',
        widgetId,
        title,
        xAxisTitle: chartData.chart.xAxisTitle,
        yAxisTitle: chartData.chart.yAxisTitle,
        series: chartData.chart.data.map((entry) => ({
          label: entry.index,
          points: entry.data.map((p) => ({ x: p.x, y: p.y })),
        })),
      };
    case 'pie':
      return {
        kind: 'series_chart',
        widgetId,
        title,
        xAxisTitle: chartData.chart.xAxisTitle,
        yAxisTitle: chartData.chart.yAxisTitle,
        series: [{ points: chartData.chart.data.map((p) => ({ x: p.label, y: p.value })) }],
      };
    case 'line':
    case 'multiple-line':
      return {
        kind: 'series_chart',
        widgetId,
        title,
        xAxisTitle: chartData.chart.xAxisTitle,
        yAxisTitle: chartData.chart.yAxisTitle,
        series: chartData.chart.data.map((s) => ({ label: s.label, points: s.data })),
      };
    case 'data-table': {
      // Mirrors ChartWidgetRouter's precedence so the snapshot matches what's actually rendered.
      const columns = (chartData.customColumnNames ?? chartData.data.columnNames).map(joinLabelPath);
      const rowLabels = (chartData.customRowNames ?? chartData.data.rowNames)
        .slice(0, MAX_DATA_TABLE_PREVIEW_ROWS)
        .map(joinLabelPath);
      return {
        kind: 'data_table',
        widgetId,
        title,
        columns,
        rowLabels,
        rowCount: chartData.data.records.length,
        previewRows: chartData.data.records.slice(0, MAX_DATA_TABLE_PREVIEW_ROWS),
      };
    }
    case 'kpi-widget':
      return {
        kind: 'stat_grid',
        widgetId,
        title,
        items: chartData.labels.map((label, i) => ({ label, value: chartData.values[i] ?? '' })),
      };
    case 'progress-bar-widget':
    case 'semi-circle-progress-widget':
      return {
        kind: 'stat_grid',
        widgetId,
        title,
        items: chartData.labels.map((label, i) => ({
          label,
          value: chartData.values[i] ?? '',
          unit: chartData.units?.[i],
        })),
      };
    default: {
      const exhaustiveCheck: never = chartData;
      throw new Error(`Unhandled chart type: ${JSON.stringify(exhaustiveCheck)}`);
    }
  }
}

/** Projects a widget's resolved CanonicalWidgetData into a compact, agent-facing snapshot entry. */
export function projectWidgetSnapshot(
  widget: WidgetConfig,
  data: CanonicalWidgetData | undefined
): WidgetSnapshotEntry {
  const title = widget.config.title ?? 'Untitled widget';

  switch (widget.type) {
    case 'chart':
      return data?.type === 'chart'
        ? projectChartSnapshot(widget.id, title, data.data)
        : { kind: 'no_data', widgetId: widget.id, title };
    case 'metric_card':
      return data?.type === 'metric_card'
        ? {
            kind: 'stat_grid',
            widgetId: widget.id,
            title,
            items: data.data.labels.map((label, i) => ({ label, value: data.data.values[i] ?? '' })),
          }
        : { kind: 'no_data', widgetId: widget.id, title };
    case 'text': {
      if (data?.type !== 'text') return { kind: 'no_data', widgetId: widget.id, title };
      const content =
        widget.config.textMode === 'markdown' ? data.data.content : extractRichTextContent(data.data.content);
      return { kind: 'text', widgetId: widget.id, title, content: truncateText(content) };
    }
    case 'quote':
      return data?.type === 'quote'
        ? { kind: 'quote', widgetId: widget.id, title, speaker: data.data.speaker, content: truncateText(data.data.content) }
        : { kind: 'no_data', widgetId: widget.id, title };
    case 'code':
    case 'rep_dashboard':
    case 'segment_overview':
    case 'seg_widget':
    case 'pj_widget':
    case 'pj_media':
    case 'pj_quote':
      return { kind: 'unsupported_widget_type', widgetId: widget.id, title, widgetType: widget.type };
    default: {
      const exhaustiveCheck: never = widget;
      throw new Error(`Unhandled widget type: ${JSON.stringify(exhaustiveCheck)}`);
    }
  }
}

/**
 * Caps a snapshot's serialized size by dropping trailing widgets until it fits
 * the byte budget. Called after all widgets are projected, not per-widget.
 */
export function capSnapshotToSizeBudget(
  snapshot: DashboardPageSnapshot,
  maxBytes: number = DEFAULT_MAX_SNAPSHOT_BYTES
): DashboardPageSnapshot {
  const byteLength = (value: DashboardPageSnapshot): number =>
    new TextEncoder().encode(JSON.stringify(value)).length;

  if (byteLength(snapshot) <= maxBytes) return snapshot;

  const widgets: DashboardPageSnapshot['widgets'] = [];
  for (const widget of snapshot.widgets) {
    const candidate: DashboardPageSnapshot = {
      ...snapshot,
      widgets: [...widgets, widget],
      truncated: true,
      omittedWidgetCount: snapshot.widgets.length - widgets.length - 1,
    };
    if (byteLength(candidate) <= maxBytes) widgets.push(widget);
  }

  return { ...snapshot, widgets, truncated: true, omittedWidgetCount: snapshot.widgets.length - widgets.length };
}
